/**
 * Vendor API Service
 * Service layer for vendor-related API calls
 *
 * B2.6'-fe-seam: `list`/`getById` gain a live-module leg (the `masters` key,
 * client.ts) executing the B0.7' contract operations against `/graphql` and
 * mapping the graph shapes back onto the legacy types (masters.gql-map.ts).
 * Signatures unchanged — ahooks wrappers and callers are transport-blind.
 *
 * CU-1a masters flip: `create` + `checkExists` join the live leg (the wizard
 * write path — vendorCreate mutation + the one-query companyByTaxId dedupe).
 * Locations/payment collection legs and update/toggle remain on the mock
 * seam pending the B2.7' completion pass (register-listed residual).
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import {
  GraphQLTransportError,
  gqlMutate,
  gqlRequest,
  throwOnUserErrors,
} from './graphql.client';
import {
  mastersOperations,
  type CompanyByTaxIdCheckResult,
  type MastersDetailVariables,
  type MastersListVariables,
  type VendorCreateResult,
  type VendorDetailPageResult,
  type VendorListPageResult,
  type VendorLocationSetActiveResult,
  type VendorSetActiveResult,
} from './masters.operations';
import {
  listConstraintsDroppedOnLive,
  mapVendorCheckExists,
  mapVendorCreatedNode,
  mapVendorDetailNode,
  mapVendorListNode,
  toFirstVariable,
  toLegacyPagination,
  toVendorCreateInput,
  toVendorFilterInput,
  VENDOR_LIST_PLUMBED_KEYS,
} from './masters.gql-map';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  VendorListResponse,
  VendorDetailResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  QuickCreateVendorRequest,
  QuickCreateVendorResponse,
  CheckExistsResponse,
  ToggleVendorStatusRequest,
  VendorListParams,
  CheckExistsParams,
  VendorLocationsResponse,
  VendorLocationResponse,
  VendorLocationListParams,
  CreateVendorLocationRequest,
  UpdateVendorLocationRequest,
  ToggleVendorLocationStatusRequest,
  ToggleVendorLocationStatusResponse,
  SetDefaultVendorLocationRequest,
  SetDefaultVendorLocationResponse,
} from '@/types/api/vendor';

/**
 * Vendor API service object
 */
export const vendorApi = {
  /**
   * GET /api/vendors
   * Fetch list of vendors with optional filtering and pagination
   */
  list: async (params?: VendorListParams): Promise<VendorListResponse> => {
    if (isLiveModule('masters')) {
      // CU-1a: search/categoryIds/isActive/country plumb into the SDL filter;
      // sort + cursor plumbing remains for the B2.7' completion pass.
      const dropped = listConstraintsDroppedOnLive(params, VENDOR_LIST_PLUMBED_KEYS);
      if (dropped.length > 0) {
        console.warn(
          `[vendorApi.list] live leg drops [${dropped.join(', ')}] until the B2.7' completion pass plumbs sort/cursor variables`
        );
      }
      const result = await gqlRequest<VendorListPageResult, MastersListVariables>(
        mastersOperations.vendorList,
        {
          first: toFirstVariable(params?.pageSize),
          after: null,
          filter: toVendorFilterInput(params) ?? null,
        }
      );
      return {
        data: result.vendors.edges.map((edge) => mapVendorListNode(edge.node)),
        pagination: toLegacyPagination(result.vendors.totalCount, params?.pageSize),
      };
    }
    const response = await apiClient.get<VendorListResponse>(API_ROUTES.VENDORS, {
      params,
    });
    return response.data;
  },

  /**
   * GET /api/vendors/:vendorId
   * Fetch single vendor details by ID
   */
  getById: async (vendorId: number): Promise<VendorDetailResponse> => {
    if (isLiveModule('masters')) {
      // Id posture: the numeric legacy id rides as-is (the L4 fixture ids are
      // numeric); the opaque-global-id divergence settles at the masters
      // freeze (masters.gql-map.ts `toLegacyNumericId` header).
      const result = await gqlRequest<VendorDetailPageResult, MastersDetailVariables>(
        mastersOperations.vendorDetail,
        { id: String(vendorId) }
      );
      if (!result.vendor) {
        throw new GraphQLTransportError({
          message: `Vendor ${vendorId} not found`,
          graphQLErrors: ['NOT_FOUND'],
          status: 404,
        });
      }
      return mapVendorDetailNode(result.vendor);
    }
    const response = await apiClient.get<VendorDetailResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}`
    );
    return response.data;
  },

  /**
   * POST /api/vendors
   * Create a new vendor
   */
  create: async (data: CreateVendorRequest): Promise<VendorDetailResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorCreateResult>(mastersOperations.vendorCreate, {
        input: toVendorCreateInput(data),
      });
      throwOnUserErrors(result.vendorCreate.userErrors, 'vendorCreate');
      if (!result.vendorCreate.vendor) {
        throw new GraphQLTransportError({
          message: 'vendorCreate returned neither a vendor nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // The mock echoes `body.phone || null` (legacy composed display phone,
      // not modeled by the graph) — the echo semantic threads through.
      return mapVendorCreatedNode(result.vendorCreate.vendor, data.phone || null);
    }
    const response = await apiClient.post<VendorDetailResponse>(
      API_ROUTES.VENDORS,
      data
    );
    return response.data;
  },

  /**
   * POST /api/vendors/quick-create
   * Quick create vendor with minimal information
   */
  quickCreate: async (
    data: QuickCreateVendorRequest
  ): Promise<QuickCreateVendorResponse> => {
    refuseUnobservableMastersWrite('vendorApi.quickCreate');
    const response = await apiClient.post<QuickCreateVendorResponse>(
      `${API_ROUTES.VENDORS}/quick-create`,
      data
    );
    return response.data;
  },

  /**
   * PUT /api/vendors/:vendorId
   * Update existing vendor
   */
  update: async (
    vendorId: number,
    data: UpdateVendorRequest
  ): Promise<VendorDetailResponse> => {
    refuseUnobservableMastersWrite('vendorApi.update');
    const response = await apiClient.put<VendorDetailResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/toggle
   * Toggle vendor active status
   *
   * LIVE (CU-1b masters activation). The boolean TRAVELS — `vendorSetActive`
   * carries `isActive: Boolean!`, so activate and DEACTIVATE are the same
   * call with a different argument. Routing this through `vendorUpdate`
   * instead would be the D-3 defect verbatim: `VendorUpdateInput` declares no
   * `isActive`, the resolver would drop it, answer `userErrors: []`, and the
   * caller would toast success over a row Postgres never moved.
   *
   * The REST leg it replaces did not merely go unread — `PATCH
   * /api/vendors/:id/toggle` was MEASURED returning HTTP 404 against the
   * branch server (2026-08-10, authenticated session), so under the live
   * masters posture vendor activation could not work at all.
   */
  toggleStatus: async (
    vendorId: number,
    isActive: boolean
  ): Promise<VendorDetailResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorSetActiveResult>(
        mastersOperations.vendorSetActive,
        { input: { vendorId: String(vendorId), isActive } }
      );
      throwOnUserErrors(result.vendorSetActive.userErrors, 'vendorSetActive');
      if (!result.vendorSetActive.vendor) {
        throw new GraphQLTransportError({
          message: 'vendorSetActive returned neither a vendor nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // The mock toggle echoes the FULL vendorDetails row, and the consumer
      // (`useVendorMutations`) puts the response straight into the store —
      // so the live leg serves the same detail projection.
      return mapVendorDetailNode(result.vendorSetActive.vendor);
    }
    const response = await apiClient.patch<VendorDetailResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/toggle`,
      { isActive } as ToggleVendorStatusRequest
    );
    return response.data;
  },

  /**
   * GET /api/vendors/check-exists
   * Check if vendor exists by country and tax ID
   */
  checkExists: async (params: CheckExistsParams): Promise<CheckExistsResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlRequest<CompanyByTaxIdCheckResult>(
        mastersOperations.companyByTaxIdCheck,
        { countryId: params.countryId, localTaxId: params.localTaxId }
      );
      return mapVendorCheckExists(result);
    }
    const response = await apiClient.get<CheckExistsResponse>(
      `${API_ROUTES.VENDORS}/check-exists`,
      { params }
    );
    return response.data;
  },

  /**
   * GET /api/vendors/:vendorId/locations
   * Fetch vendor locations by vendor ID
   */
  getLocations: async (
    vendorId: number,
    params?: VendorLocationListParams
  ): Promise<VendorLocationsResponse> => {
    const response = await apiClient.get<VendorLocationsResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations`,
      { params }
    );
    return response.data;
  },

  /**
   * GET /api/vendors/:vendorId/locations/:locationId
   * Fetch single vendor location by ID
   */
  getLocationById: async (
    vendorId: number,
    locationId: number
  ): Promise<VendorLocationResponse> => {
    const response = await apiClient.get<VendorLocationResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations/${locationId}`
    );
    return response.data;
  },

  /**
   * POST /api/vendors/:vendorId/locations
   * Create a new location for a vendor
   */
  createLocation: async (
    vendorId: number,
    data: Omit<CreateVendorLocationRequest, 'vendorId'>
  ): Promise<VendorLocationResponse> => {
    refuseUnobservableMastersWrite('vendorApi.createLocation');
    const requestData: CreateVendorLocationRequest = {
      ...data,
      vendorId,
    };
    const response = await apiClient.post<VendorLocationResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations`,
      requestData
    );
    return response.data;
  },

  /**
   * PUT /api/vendors/:vendorId/locations/:locationId
   * Update an existing vendor location
   */
  updateLocation: async (
    vendorId: number,
    locationId: number,
    data: UpdateVendorLocationRequest
  ): Promise<VendorLocationResponse> => {
    refuseUnobservableMastersWrite('vendorApi.updateLocation');
    const response = await apiClient.put<VendorLocationResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations/${locationId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/locations/:locationId/toggle
   * Toggle vendor location active status
   *
   * LIVE (CU-1b masters activation) — `vendorLocationSetActive`, the boolean
   * travelling.
   *
   * MEASURED, NOT ASSUMED: unlike `VendorUpdateInput`, the SATELLITE input
   * `VendorLocationUpdateInput` DOES declare `isActive` (frozen SDL,
   * 2026-08-10) — so folding this into `updateLocation` would actually
   * write, not silently drop. It still rides the setter, because `update`
   * carries the whole row: routing a lifecycle toggle through it makes the
   * toggle depend on the caller having every other field right, and turns it
   * into a full-row overwrite. `mastersActivationWrite.test.ts` pins both
   * halves of that asymmetry so neither claim can rot.
   */
  toggleLocationStatus: async (
    vendorId: number,
    locationId: number,
    isActive: boolean
  ): Promise<ToggleVendorLocationStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorLocationSetActiveResult>(
        mastersOperations.vendorLocationSetActive,
        { input: { vendorId: String(vendorId), locationId, isActive } }
      );
      throwOnUserErrors(
        result.vendorLocationSetActive.userErrors,
        'vendorLocationSetActive'
      );
      const location = result.vendorLocationSetActive.location;
      if (!location) {
        throw new GraphQLTransportError({
          message: 'vendorLocationSetActive returned neither a location nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // The LEAN legacy echo — `ToggleVendorLocationStatusResponse` is
      // {message, locationId, isActive} and no consumer reads past it.
      return {
        message: 'Status updated',
        locationId: location.locationId,
        isActive: location.isActive,
      };
    }
    const response = await apiClient.patch<ToggleVendorLocationStatusResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations/${locationId}/toggle`,
      { isActive } as ToggleVendorLocationStatusRequest
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/locations/:locationId/set-default
   * Set vendor location as default receiving address
   */
  setDefaultLocation: async (
    vendorId: number,
    locationId: number,
    isDefault: boolean
  ): Promise<SetDefaultVendorLocationResponse> => {
    refuseUnobservableMastersWrite('vendorApi.setDefaultLocation');
    const response = await apiClient.patch<SetDefaultVendorLocationResponse>(
      `${API_ROUTES.VENDORS}/${vendorId}/locations/${locationId}/set-default`,
      { isDefault } as SetDefaultVendorLocationRequest
    );
    return response.data;
  },
};

export default vendorApi;
